import { Link } from "@remix-run/react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { useState } from "react";
import styles from "./features.module.scss";

const PURPLE_CHECKMARK =
  "https://img.icons8.com/?size=100&id=59850&format=png&color=6A67FF";

const featureCards = [
  {
    title: "Passkeys",
    description:
      "Offer phishing-resistant sign-in with browser-native passkeys and modern authentication flows that fit naturally into the Strapi admin experience.",
    image:
      "https://res.cloudinary.com/dreb6fx0x/image/upload/v1773240515/login-with-passkeys_jn7jef.png",
    accent: "Authentication",
    highlights: [
      "Browser-native passkey enrollment for modern phishing-resistant login",
      "Fits directly into the Strapi admin auth journey",
      "Pairs cleanly with trusted devices and companion flows",
    ],
  },
  {
    title: "MFA enforcement policies",
    description:
      "Require MFA per role, define allowed enrollment methods, and give new users a configurable grace period before enforcement kicks in.",
    image:
      "https://res.cloudinary.com/dreb6fx0x/image/upload/v1773233933/mfa-enforcement-policies_y1u6rk.png",
    accent: "Governance",
    highlights: [
      "Set enforcement rules per role",
      "Allow only approved factors during enrollment",
      "Use grace periods before hard enforcement begins",
    ],
  },
  {
    title: "Password expiration policy",
    description:
      "Force password rotation after a configurable number of days and redirect users into the reset-password flow when their password has expired.",
    image:
      "https://res.cloudinary.com/dreb6fx0x/image/upload/v1773234683/password-expiration-policy_edxiwr.png",
    accent: "Lifecycle",
    highlights: [
      "Define a maximum password age in days",
      "Push users into the reset flow once a password expires",
      "Keep password hygiene aligned with internal policy",
    ],
  },
  {
    title: "Breached password detection",
    description:
      "Check passwords against the Have I Been Pwned breach corpus during login and notify administrators when compromised credentials are detected.",
    image:
      "https://res.cloudinary.com/dreb6fx0x/image/upload/v1773234696/have-ive-been-pwned-integration-2_x3ue3o.png",
    accent: "Detection",
    highlights: [
      "Check credentials against the HIBP breach corpus on login",
      "Notify administrators when compromised passwords are found",
      "Adds password-risk visibility without changing login UX",
    ],
  },
  {
    title: "Trusted devices",
    description:
      "Reduce repeated MFA prompts on known devices while still giving administrators and users visibility into trusted sessions.",
    image:
      "https://res.cloudinary.com/dreb6fx0x/image/upload/v1773234688/trusted-devices_q0iffp.png",
    accent: "Usability",
    highlights: [
      "Reduce MFA friction on known devices",
      "Give users visibility into remembered devices",
      "Allow device revocation when trust should be removed",
    ],
  },
  {
    title: "Companion app and magic login",
    description:
      "Support smoother cross-device authentication flows with QR-based sign-in and companion-powered passkey experiences.",
    image:
      "https://res.cloudinary.com/dreb6fx0x/image/upload/v1773232897/headlockr-magic-login_lieku5.png",
    accent: "Coming soon",
    highlights: [
      "QR-driven sign-in across devices",
      "Companion-powered passkey experiences",
      "A smoother path for modern login journeys in Strapi",
    ],
  },
];

export default function ProductUpdates() {
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const expandedFeature = featureCards.find((feature) => feature.title === activeFeature) ?? null;

  return (
    <div className={styles.wrapper}>
      <div className="row align-items-start g-4">
        <div className="col-xl-4">
          <div className={`${styles.storyCard} ${styles.storyCardPrimary}`}>
            <div className={styles.eyebrow}>Headlockr 5 - Available now</div>
            <h2 className="mb-3">
              Secure Strapi with MFA. <span className="text-tertiary">Then enforce it properly</span>
            </h2>
            <p className="text-gray-neutral-800 mb-4">
              Headlockr started as a secure MFA layer for Strapi. It now extends into policy-driven admin security:
              enforce enrollment per role, detect compromised passwords, reduce prompt fatigue with trusted devices,
              and integrate natively with modern Strapi builds.
            </p>

            <div className={styles.coreList}>
              {[
                "SMS, TOTP, email and backup codes",
                "Passkeys, trusted devices and companion flows",
                "Native Strapi integration",
                "Policy-driven MFA enforcement per role"
              ].map((feature) => (
                <div key={feature} className={styles.coreItem}>
                  <span className={styles.coreDot}></span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="d-flex flex-wrap gap-2 mt-4">
              <Link to="/#pricing" className="btn btn-tertiary">
                Start protecting now
              </Link>
              <a href="https://docs.headlockr.io" className="btn btn-outline-light">
                Read the docs
              </a>
            </div>
          </div>
        </div>
        <div className="col-xl-8">
          <LayoutGroup id="headlockr-product-updates">
            <div className="row g-4">
              {featureCards.map((feature) => (
                <div
                  key={feature.title}
                  className="col-md-6"
                >
                  <motion.button
                    type="button"
                    layoutId={`feature-card-${feature.title}`}
                    className={`${styles.featureCard} ${activeFeature === feature.title ? styles.featureCardActive : ""}`}
                    onClick={() => setActiveFeature(feature.title)}
                  >
                    <div className={styles.featureImageWrap}>
                      <motion.img
                        layoutId={`feature-image-${feature.title}`}
                        src={feature.image}
                        alt={feature.title}
                        className={styles.featureImage}
                      />
                      <motion.span layoutId={`feature-accent-${feature.title}`} className={styles.accentBadge}>
                        {feature.accent}
                      </motion.span>
                      <span className={styles.expandButton} aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <path d="M8 16L16 8M10 8h6v6" />
                        </svg>
                      </span>
                    </div>
                    <div className={styles.featureBody}>
                      <h4 className="mb-2">{feature.title}</h4>
                      <p className="text-gray-neutral-800 mb-0">
                        {feature.description}
                      </p>
                    </div>
                  </motion.button>
                </div>
              ))}
            </div>

            <AnimatePresence>
              {expandedFeature ? (
                <>
                  <motion.button
                    type="button"
                    className={styles.expandedBackdrop}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setActiveFeature(null)}
                    aria-label="Close feature details"
                  />

                  <div className={styles.expandedPanelShell}>
                    <motion.div
                      layoutId={`feature-card-${expandedFeature.title}`}
                      className={styles.expandedPanel}
                    >
                      <button
                        type="button"
                        className={styles.expandedClose}
                        aria-label={`Collapse details for ${expandedFeature.title}`}
                        onClick={() => setActiveFeature(null)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M8 8l8 8M16 8l-8 8" />
                        </svg>
                      </button>

                      <div className="row g-4 align-items-start">
                        <div className="col-lg-5">
                          <div className={styles.expandedCopy}>
                            <motion.div
                              layoutId={`feature-accent-${expandedFeature.title}`}
                              className={styles.expandedAccent}
                            >
                              {expandedFeature.accent}
                            </motion.div>
                            <h3 className={styles.expandedTitle}>{expandedFeature.title}</h3>
                            <p className={styles.expandedDescription}>
                              {expandedFeature.description}
                            </p>

                            <div className={styles.expandedList}>
                              {expandedFeature.highlights.map((highlight) => (
                                <motion.div
                                  key={highlight}
                                  className={styles.expandedItem}
                                  initial={{ opacity: 0, y: 12 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 8 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <img
                                    src={PURPLE_CHECKMARK}
                                    alt=""
                                    aria-hidden="true"
                                    className={styles.expandedCheck}
                                  />
                                  <span>{highlight}</span>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-7">
                          <div className={styles.expandedVisualWrap}>
                            <motion.img
                              layoutId={`feature-image-${expandedFeature.title}`}
                              src={expandedFeature.image}
                              alt={expandedFeature.title}
                              className={styles.expandedVisual}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </>
              ) : null}
            </AnimatePresence>
          </LayoutGroup>
        </div>
      </div>
    </div>
  );
}
